import { useState, useMemo } from "react";
import { MEASURES, type MeasureId } from "../config/metrics";
import { DIMENSIONS, type DimensionId } from "../config/dimensions";
import { useData } from "../data/DataContext";
import { useControl } from "../control/ControlContext";
import { type PlotChartType, type PlotConfig, type TimeGrain } from "../config/plots";
import type { BoxplotData, DimensionSeries, PreparedData } from "../config/data";
import SearchableSelect from "./SearchableSelect";
import { PlotSection } from "./Plots";

type DistributionType = "normal" | "log-normal" | "logit-normal";

interface CustomPlotEntry {
  id: string;
  measureId: MeasureId;
  chartType: PlotChartType;
  dimension?: DimensionId;
  distribution?: DistributionType;
}

const MEASURE_OPTIONS = MEASURES.map(m => ({ value: m.id, label: m.label })).sort((a, b) =>
  a.label.localeCompare(b.label),
);

const SINGLE_DIMENSIONS = DIMENSIONS.filter(d => d.id !== "all" && d.dim2Field === null);
const DIMENSION_OPTIONS = SINGLE_DIMENSIONS.map(d => ({ value: d.id, label: d.label }));

const DISTRIBUTION_OPTIONS: { value: DistributionType; label: string }[] = [
  { value: "log-normal", label: "Log-normal (geometric mean)" },
  { value: "logit-normal", label: "Logit-normal (logistic mean)" },
  { value: "normal", label: "Normal (arithmetic mean)" },
];

const ALL_CHART_TYPE_OPTIONS: { value: PlotChartType; label: string }[] = [
  { value: "xmr", label: "XmR" },
  { value: "xmr-log", label: "XmR (log scale)" },
  { value: "x", label: "X (individuals)" },
  { value: "ewma", label: "EWMA" },
  { value: "line", label: "Line" },
  { value: "boxplot", label: "Boxplot by dimension" },
  { value: "distribution-xmr", label: "Distribution XmR by dimension" },
];

function needsDimension(chartType: PlotChartType) {
  return chartType === "boxplot" || chartType === "distribution-xmr";
}

function needsDistribution(chartType: PlotChartType) {
  return chartType === "distribution-xmr";
}

// Compute BoxplotData from DimensionSeries already in preparedData
function computeBoxplot(series: DimensionSeries, lowerPct: number, upperPct: number): BoxplotData {
  // Build a map: dateKey -> { dimVal -> value }
  const dateMap = new Map<string, Map<string, number>>();
  for (const [dimVal, ts] of Object.entries(series)) {
    for (let i = 0; i < ts.timestamps.length; i++) {
      const v = ts.data[i];
      if (v == null) continue;
      const dateKey = ts.timestamps[i].toISOString().split("T")[0];
      if (!dateMap.has(dateKey)) dateMap.set(dateKey, new Map());
      dateMap.get(dateKey)!.set(dimVal, v);
    }
  }

  const sortedDates = Array.from(dateMap.keys()).sort();
  const timestamps: Date[] = [];
  const boxplotValues: [number, number, number, number, number][] = [];
  const dimensionValuesByDate: Record<string, { name: string; value: number }[]> = {};
  const aggByDim = new Map<string, number[]>();

  for (const dateKey of sortedDates) {
    const byDim = dateMap.get(dateKey)!;
    const entries = Array.from(byDim.entries()).map(([name, value]) => ({ name, value }));
    if (entries.length === 0) continue;

    const sorted = entries.map(e => e.value).sort((a, b) => a - b);
    timestamps.push(new Date(dateKey));
    boxplotValues.push([
      pct(sorted, lowerPct),
      pct(sorted, 0.25),
      pct(sorted, 0.5),
      pct(sorted, 0.75),
      pct(sorted, upperPct),
    ]);
    dimensionValuesByDate[dateKey] = entries;
    for (const { name, value } of entries) {
      if (!aggByDim.has(name)) aggByDim.set(name, []);
      aggByDim.get(name)!.push(value);
    }
  }

  const dimensionValues = Array.from(aggByDim.entries()).map(([name, vals]) => ({
    name,
    value: vals.reduce((a, b) => a + b, 0) / vals.length,
  }));

  return {
    timestamps,
    boxplotValues,
    extremeLabels: { lower: `${Math.round(lowerPct * 100)}%`, upper: `${Math.round(upperPct * 100)}%` },
    dimensionValues,
    dimensionValuesByDate,
  };
}

function pct(sorted: number[], p: number): number {
  const idx = p * (sorted.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

// Inject computed boxplot data into a copy of preparedData under the custom plot's id
function augmentPreparedData(preparedData: PreparedData, entry: CustomPlotEntry): PreparedData {
  if (entry.chartType !== "boxplot" || !entry.dimension) return preparedData;
  const series = preparedData[entry.measureId]?.[entry.dimension] as DimensionSeries | undefined;
  if (!series) return preparedData;
  const boxplotData = computeBoxplot(series, 0.05, 0.95);
  return {
    ...preparedData,
    [entry.measureId]: {
      ...preparedData[entry.measureId],
      [entry.id]: boxplotData,
    },
  };
}

function buildPlotConfig(entry: CustomPlotEntry): PlotConfig {
  const measure = MEASURES.find(m => m.id === entry.measureId)!;
  const base: PlotConfig = { id: entry.id, measureId: entry.measureId, label: measure.label, chart: entry.chartType };
  if (entry.chartType === "boxplot" && entry.dimension) {
    return { ...base, boxplot: { aggregateDimension: entry.dimension, lowerExtreme: 0.05, upperExtreme: 0.95 } };
  }
  if (entry.chartType === "distribution-xmr" && entry.dimension && entry.distribution) {
    return { ...base, distributionXmr: { dimension: entry.dimension, distribution: entry.distribution } };
  }
  return base;
}

export default function CustomPlots() {
  const { preparedData } = useData();
  const { timeRange, grainOverride } = useControl();
  const globalGrain = useMemo((): TimeGrain => {
    if (grainOverride) return grainOverride;
    if (!timeRange.start || !timeRange.end) return "day";
    const diffDays = (timeRange.end.getTime() - timeRange.start.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays > 63 ? "week" : "day";
  }, [grainOverride, timeRange]);

  const [entries, setEntries] = useState<CustomPlotEntry[]>([]);
  const [pendingMeasure, setPendingMeasure] = useState<MeasureId | "">("");
  const [pendingChart, setPendingChart] = useState<PlotChartType | "">("");
  const [pendingDimension, setPendingDimension] = useState<DimensionId | "">("");
  const [pendingDistribution, setPendingDistribution] = useState<DistributionType | "">("");

  const selectedMeasure = useMemo(() => MEASURES.find(m => m.id === pendingMeasure), [pendingMeasure]);

  const chartTypeOptions = selectedMeasure ? ALL_CHART_TYPE_OPTIONS : [];

  const showDimensionPicker = !!pendingChart && needsDimension(pendingChart as PlotChartType);
  const showDistributionPicker = !!pendingChart && needsDistribution(pendingChart as PlotChartType);

  const canAdd =
    !!pendingMeasure &&
    !!pendingChart &&
    (!showDimensionPicker || !!pendingDimension) &&
    (!showDistributionPicker || !!pendingDistribution);

  const handleMeasureChange = (id: string) => {
    setPendingMeasure(id as MeasureId);
    setPendingChart("");
    setPendingDimension("");
    setPendingDistribution("");
  };

  const handleChartChange = (t: string) => {
    setPendingChart(t as PlotChartType);
    setPendingDimension("");
    setPendingDistribution("");
  };

  const handleAdd = () => {
    if (!canAdd) return;
    const id = `custom-${pendingMeasure}-${pendingChart}-${Date.now()}`;
    const entry: CustomPlotEntry = {
      id,
      measureId: pendingMeasure as MeasureId,
      chartType: pendingChart as PlotChartType,
      dimension: pendingDimension ? (pendingDimension as DimensionId) : undefined,
      distribution: pendingDistribution ? (pendingDistribution as DistributionType) : undefined,
    };
    setEntries(prev => [...prev, entry]);
    setPendingMeasure("");
    setPendingChart("");
    setPendingDimension("");
    setPendingDistribution("");
  };

  return (
    <div>
      <div className="custom-plots-toolbar">
        <SearchableSelect
          value={pendingMeasure}
          options={MEASURE_OPTIONS}
          onChange={handleMeasureChange}
          placeholder="Select metric…"
          className="custom-plots-toolbar__metric-select"
          dropUp
        />
        <SearchableSelect
          value={pendingChart}
          options={chartTypeOptions}
          onChange={handleChartChange}
          placeholder="Chart type…"
          className="custom-plots-toolbar__chart-select"
          dropUp
        />
        {showDimensionPicker && (
          <SearchableSelect
            value={pendingDimension}
            options={DIMENSION_OPTIONS}
            onChange={v => setPendingDimension(v as DimensionId)}
            placeholder="Group by…"
            className="custom-plots-toolbar__chart-select"
            dropUp
          />
        )}
        {showDistributionPicker && (
          <SearchableSelect
            value={pendingDistribution}
            options={DISTRIBUTION_OPTIONS}
            onChange={v => setPendingDistribution(v as DistributionType)}
            placeholder="Distribution…"
            className="custom-plots-toolbar__distribution-select"
            dropUp
          />
        )}
        <button type="button" className="custom-plots-toolbar__add-btn" onClick={handleAdd} disabled={!canAdd}>
          Add chart
        </button>
      </div>
      {preparedData && (
        <div className="plots">
          {entries.map(entry => {
            const plot = buildPlotConfig(entry);
            const augmented = augmentPreparedData(preparedData, entry);
            return (
              <div key={entry.id} className="custom-plot-item">
                <div className="custom-plot-item__bar">
                  <button
                    type="button"
                    className="custom-plot-item__remove"
                    onClick={() => setEntries(prev => prev.filter(e => e.id !== entry.id))}
                  >
                    Remove
                  </button>
                </div>
                <PlotSection plot={plot} preparedData={augmented} globalGrain={globalGrain} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
